import { useState, useEffect, useCallback } from "react";
import { AdminLayout } from "@/components/shared/AdminLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  getAllMarketplaceOrders, updateMarketplaceOrder, cancelMarketplaceOrder, deleteMarketplaceOrder,
  adminCreateMarketplaceOrder, getDealersForOrder,
  getInventoryProducts, getAvailableSerialsForOrder,
} from "@/lib/api";
import { toast } from "sonner";
import {
  ShoppingCart, Loader2, Truck, Package, Clock, CheckCircle, XCircle,
  Plus, Trash2, Hash, ChevronDown, ChevronUp, Edit, PlusCircle, AlertCircle,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const statusColors = {
  pending: "bg-amber-100 text-amber-700",
  processing: "bg-blue-100 text-blue-700",
  fulfilled: "bg-emerald-100 text-emerald-700",
  cancelled: "bg-red-100 text-red-700",
};
const statusIcons = {
  pending: Clock,
  processing: Package,
  fulfilled: CheckCircle,
  cancelled: XCircle,
};

// ===== Serial Picker Component =====
function SerialPicker({ orderId, productId, productName, quantity, assigned, onAssign }) {
  const [serials, setSerials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const fetchSerials = async () => {
    setLoading(true);
    try {
      const data = await getAvailableSerialsForOrder(orderId, productId);
      setSerials(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (open) fetchSerials();
  }, [open]);

  const toggle = (serialId) => {
    const current = assigned || [];
    if (current.includes(serialId)) {
      onAssign(productId, current.filter((s) => s !== serialId));
    } else {
      if (current.length >= quantity) {
        toast.error(`Max ${quantity} serials for this item`);
        return;
      }
      onAssign(productId, [...current, serialId]);
    }
  };

  return (
    <div className="mt-2">
      <Button
        type="button"
        size="sm"
        variant="outline"
        className="text-xs gap-1"
        onClick={() => setOpen(!open)}
        data-testid={`serial-picker-toggle-${productId}`}
      >
        <Hash className="w-3 h-3" />
        Assign Serials ({(assigned || []).length}/{quantity})
        {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
      </Button>
      {open && (
        <div className="mt-2 border rounded-md p-2 bg-slate-50 max-h-40 overflow-y-auto">
          {loading ? (
            <div className="flex justify-center py-2"><Loader2 className="w-4 h-4 animate-spin" /></div>
          ) : serials.length === 0 ? (
            <p className="text-xs text-slate-400 text-center py-2">No available serials</p>
          ) : (
            <div className="space-y-1">
              {serials.map((sn) => {
                const isSelected = (assigned || []).includes(sn.id);
                return (
                  <label
                    key={sn.id}
                    className={`flex items-center gap-2 px-2 py-1 rounded cursor-pointer text-xs ${
                      isSelected ? "bg-teal/10 text-teal font-medium" : "hover:bg-slate-100"
                    }`}
                    data-testid={`serial-option-${sn.id}`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggle(sn.id)}
                      className="rounded border-slate-300"
                    />
                    <span className="font-mono">{sn.serial_number}</span>
                  </label>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ===== Edit Order Dialog =====
function EditOrderDialog({ order, open, onClose, onSaved, products }) {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("");
  const [tracking, setTracking] = useState("");
  const [notes, setNotes] = useState("");
  const [serialMap, setSerialMap] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [addProductId, setAddProductId] = useState("");
  const [cancelDialog, setCancelDialog] = useState(false);
  const [cancelChoice, setCancelChoice] = useState("restock");
  const [cancelReason, setCancelReason] = useState("");
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    if (order) {
      setItems(order.items?.map((it) => ({ ...it })) || []);
      setStatus(order.status || "pending");
      setTracking(order.tracking_number || "");
      setNotes(order.notes || "");
      const sm = {};
      (order.items || []).forEach((it) => {
        if (it.assigned_serials?.length) sm[it.product_id] = [...it.assigned_serials];
      });
      setSerialMap(sm);
    }
  }, [order]);

  const updateQty = (idx, qty) => {
    const next = [...items];
    next[idx].quantity = Math.max(1, parseInt(qty) || 1);
    setItems(next);
  };

  const removeItem = (idx) => {
    const removed = items[idx];
    setItems(items.filter((_, i) => i !== idx));
    if (removed) {
      const next = { ...serialMap };
      delete next[removed.product_id];
      setSerialMap(next);
    }
  };

  const addProduct = () => {
    if (!addProductId) return;
    if (items.some((it) => it.product_id === addProductId)) {
      toast.error("Product already in order");
      return;
    }
    const prod = products.find((p) => p.id === addProductId);
    if (!prod) return;
    setItems([...items, {
      product_id: prod.id,
      product_name: prod.name,
      sku: prod.sku || "",
      quantity: 1,
      unit_price: prod.retail_price,
      assigned_serials: [],
    }]);
    setAddProductId("");
  };

  const handleSerialAssign = (productId, serialIds) => {
    setSerialMap((prev) => ({ ...prev, [productId]: serialIds }));
  };

  const handleSave = async () => {
    if (items.length === 0) {
      toast.error("Order must have at least one item");
      return;
    }
    // If admin is changing status to "cancelled" on a previously-non-cancelled order,
    // route through the dedicated cancel flow that asks: restock vs remove (with reason).
    if (status === "cancelled" && order?.status !== "cancelled") {
      setCancelChoice("restock");
      setCancelReason("");
      setCancelDialog(true);
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        items: items.map((it) => ({
          product_id: it.product_id,
          quantity: it.quantity,
          unit_price: it.unit_price,
          assigned_serials: serialMap[it.product_id] || it.assigned_serials || [],
        })),
        assign_serials: serialMap,
        status,
        tracking_number: tracking,
        notes,
      };
      await updateMarketplaceOrder(order.id, payload);
      toast.success("Order updated");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error updating order");
    } finally { setSubmitting(false); }
  };

  const handleConfirmCancel = async () => {
    if (cancelChoice === "remove" && !cancelReason.trim()) {
      toast.error("Please state the reason for removing this inventory");
      return;
    }
    setCancelling(true);
    try {
      const res = await cancelMarketplaceOrder(order.id, cancelChoice === "restock", cancelReason.trim());
      toast.success(res.message || "Order cancelled");
      setCancelDialog(false);
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to cancel order");
    } finally { setCancelling(false); }
  };

  const isClosed = order?.status === "fulfilled" || order?.status === "cancelled";

  const availableProducts = products.filter(
    (p) => p.active && !items.some((it) => it.product_id === p.id)
  );

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Edit className="w-5 h-5" />
            Edit Order {order?.order_number}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-5">
          {/* Dealer info */}
          <div className="bg-slate-50 rounded-md p-3 text-sm">
            <span className="text-slate-500">Dealer:</span>{" "}
            <span className="font-medium text-navy">{order?.dealer_name}</span>{" "}
            <span className="text-slate-400">({order?.dealer_email})</span>
          </div>

          {/* Status + Tracking */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Status</Label>
              <Select value={status} onValueChange={setStatus} disabled={isClosed}>
                <SelectTrigger data-testid="edit-order-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="processing">Processing</SelectItem>
                  <SelectItem value="fulfilled">Fulfilled</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Tracking Number</Label>
              <Input
                value={tracking}
                onChange={(e) => setTracking(e.target.value)}
                placeholder="Enter tracking..."
                data-testid="edit-order-tracking"
              />
            </div>
          </div>

          <div>
            <Label>Notes</Label>
            <Input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Order notes..."
              data-testid="edit-order-notes"
            />
          </div>

          {/* Order Items */}
          <div>
            <div className="flex justify-between items-center mb-3">
              <Label className="text-base font-semibold">Order Items</Label>
            </div>

            <div className="space-y-3">
              {items.map((item, idx) => (
                <div key={`${item.product_id}-${idx}`} className="border rounded-lg p-3 bg-white">
                  <div className="flex justify-between items-start gap-3">
                    <div className="flex-1">
                      <p className="font-medium text-navy text-sm">{item.product_name}</p>
                      {item.sku && <p className="text-xs text-slate-400">SKU: {item.sku}</p>}
                      <p className="text-xs text-slate-500 mt-0.5">${item.unit_price?.toFixed(2)} each</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs">Qty:</Label>
                      <Input
                        type="number"
                        min={1}
                        value={item.quantity}
                        onChange={(e) => updateQty(idx, e.target.value)}
                        className="w-20 h-8 text-sm"
                        disabled={isClosed}
                        data-testid={`edit-item-qty-${item.product_id}`}
                      />
                      {!isClosed && (
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8 text-red-500 hover:text-red-700"
                          onClick={() => removeItem(idx)}
                          data-testid={`remove-item-${item.product_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                  <p className="text-xs font-medium text-right text-slate-600 mt-1">
                    Subtotal: ${(item.unit_price * item.quantity).toFixed(2)}
                  </p>
                  {/* Serial assignment */}
                  {order?.id && (
                    <SerialPicker
                      orderId={order.id}
                      productId={item.product_id}
                      productName={item.product_name}
                      quantity={item.quantity}
                      assigned={serialMap[item.product_id] || item.assigned_serials || []}
                      onAssign={handleSerialAssign}
                    />
                  )}
                </div>
              ))}
            </div>

            {/* Add product */}
            {!isClosed && (
              <div className="flex items-end gap-2 mt-3 pt-3 border-t">
                <div className="flex-1">
                  <Label className="text-xs">Add Product</Label>
                  <Select value={addProductId} onValueChange={setAddProductId}>
                    <SelectTrigger data-testid="add-product-select">
                      <SelectValue placeholder="Select product..." />
                    </SelectTrigger>
                    <SelectContent>
                      {availableProducts.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.name} (${p.retail_price?.toFixed(2)}) — Stock: {p.quantity_in_stock}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  size="sm"
                  onClick={addProduct}
                  disabled={!addProductId}
                  className="bg-teal hover:bg-teal/90"
                  data-testid="add-product-btn"
                >
                  <Plus className="w-4 h-4 mr-1" /> Add
                </Button>
              </div>
            )}

            {/* Total */}
            <div className="flex justify-end mt-3 pt-2 border-t">
              <p className="text-lg font-bold text-navy">
                Total: ${items.reduce((sum, it) => sum + it.unit_price * it.quantity, 0).toFixed(2)}
              </p>
            </div>
          </div>

          <Button
            onClick={handleSave}
            disabled={submitting}
            className="w-full bg-teal hover:bg-teal/90"
            data-testid="save-order-btn"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Changes"}
          </Button>
        </div>
      </DialogContent>

      {/* Cancel Order — Restock or Remove Dialog */}
      <Dialog open={cancelDialog} onOpenChange={(o) => !cancelling && setCancelDialog(o)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertCircle className="w-5 h-5" /> Cancel Order — Inventory Decision
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              You&rsquo;re cancelling order <span className="font-mono font-medium">{order?.order_number}</span>.
              What should happen to the items being released from this order?
            </p>
            <div className="space-y-2" data-testid="cancel-choice-group">
              <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${cancelChoice === "restock" ? "border-teal bg-teal/5" : "border-slate-200 hover:border-slate-300"}`}>
                <input
                  type="radio"
                  name="cancel-choice"
                  value="restock"
                  checked={cancelChoice === "restock"}
                  onChange={() => setCancelChoice("restock")}
                  className="mt-1 accent-teal"
                  data-testid="cancel-choice-restock"
                />
                <div>
                  <p className="font-medium text-navy text-sm">Put back into stock</p>
                  <p className="text-xs text-slate-500">Inventory returns to On-Hand. Recommended for most cancellations.</p>
                </div>
              </label>
              <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${cancelChoice === "remove" ? "border-red-400 bg-red-50" : "border-slate-200 hover:border-slate-300"}`}>
                <input
                  type="radio"
                  name="cancel-choice"
                  value="remove"
                  checked={cancelChoice === "remove"}
                  onChange={() => setCancelChoice("remove")}
                  className="mt-1 accent-red-500"
                  data-testid="cancel-choice-remove"
                />
                <div>
                  <p className="font-medium text-navy text-sm">Remove from inventory permanently</p>
                  <p className="text-xs text-slate-500">Use when items are damaged, lost, fraudulent, or otherwise unsellable. A reason is required.</p>
                </div>
              </label>
            </div>
            {cancelChoice === "remove" && (
              <div>
                <Label className="text-sm">Reason for removal *</Label>
                <Input
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  placeholder="e.g. damaged in transit, lost, fraudulent, write-off..."
                  className="mt-1"
                  data-testid="cancel-removal-reason"
                  autoFocus
                />
              </div>
            )}
            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" onClick={() => setCancelDialog(false)} disabled={cancelling} data-testid="cancel-dialog-back">
                Back
              </Button>
              <Button
                className={cancelChoice === "remove" ? "bg-red-600 hover:bg-red-700 text-white" : "bg-teal hover:bg-teal/90 text-white"}
                onClick={handleConfirmCancel}
                disabled={cancelling || (cancelChoice === "remove" && !cancelReason.trim())}
                data-testid="cancel-dialog-confirm"
              >
                {cancelling ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                {cancelChoice === "restock" ? "Cancel & Restock" : "Cancel & Remove Inventory"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}

// ===== Create Order Dialog =====
function CreateOrderDialog({ open, onClose, onCreated, dealers, products }) {
  const [dealerId, setDealerId] = useState("");
  const [items, setItems] = useState([]);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [addProductId, setAddProductId] = useState("");

  useEffect(() => {
    if (open) { setDealerId(""); setItems([]); setNotes(""); setAddProductId(""); }
  }, [open]);

  const addProduct = () => {
    if (!addProductId) return;
    if (items.some((it) => it.product_id === addProductId)) {
      toast.error("Product already added");
      return;
    }
    const prod = products.find((p) => p.id === addProductId);
    if (!prod) return;
    setItems([...items, {
      product_id: prod.id,
      product_name: prod.name,
      sku: prod.sku || "",
      quantity: 1,
      unit_price: prod.retail_price,
    }]);
    setAddProductId("");
  };

  const updateQty = (idx, qty) => {
    const next = [...items];
    next[idx].quantity = Math.max(1, parseInt(qty) || 1);
    setItems(next);
  };

  const removeItem = (idx) => setItems(items.filter((_, i) => i !== idx));

  const handleCreate = async () => {
    if (!dealerId) return toast.error("Select a dealer");
    if (items.length === 0) return toast.error("Add at least one product");
    setSubmitting(true);
    try {
      await adminCreateMarketplaceOrder({
        dealer_id: dealerId,
        items: items.map((it) => ({ product_id: it.product_id, quantity: it.quantity })),
        notes,
      });
      toast.success("Order created");
      onCreated();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error creating order");
    } finally { setSubmitting(false); }
  };

  const total = items.reduce((s, it) => s + it.unit_price * it.quantity, 0);
  const availableProducts = products.filter(
    (p) => p.active && p.quantity_in_stock > 0 && !items.some((it) => it.product_id === p.id)
  );

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PlusCircle className="w-5 h-5" />
            Create Order for Dealer
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-5">
          {/* Dealer selection */}
          <div>
            <Label>Select Dealer</Label>
            <Select value={dealerId} onValueChange={setDealerId}>
              <SelectTrigger data-testid="create-order-dealer-select">
                <SelectValue placeholder="Choose a dealer..." />
              </SelectTrigger>
              <SelectContent>
                {dealers.map((d) => (
                  <SelectItem key={d.id} value={d.id}>
                    {d.first_name} {d.last_name} ({d.email})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Add products */}
          <div>
            <Label className="text-base font-semibold">Products</Label>
            <div className="space-y-3 mt-2">
              {items.map((item, idx) => (
                <div key={item.product_id} className="border rounded-lg p-3 bg-white">
                  <div className="flex justify-between items-start gap-3">
                    <div className="flex-1">
                      <p className="font-medium text-navy text-sm">{item.product_name}</p>
                      {item.sku && <p className="text-xs text-slate-400">SKU: {item.sku}</p>}
                      <p className="text-xs text-slate-500">${item.unit_price?.toFixed(2)} each</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs">Qty:</Label>
                      <Input
                        type="number"
                        min={1}
                        value={item.quantity}
                        onChange={(e) => updateQty(idx, e.target.value)}
                        className="w-20 h-8 text-sm"
                        data-testid={`create-item-qty-${item.product_id}`}
                      />
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 text-red-500 hover:text-red-700"
                        onClick={() => removeItem(idx)}
                        data-testid={`create-remove-item-${item.product_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <p className="text-xs font-medium text-right text-slate-600 mt-1">
                    Subtotal: ${(item.unit_price * item.quantity).toFixed(2)}
                  </p>
                </div>
              ))}
            </div>

            <div className="flex items-end gap-2 mt-3 pt-3 border-t">
              <div className="flex-1">
                <Label className="text-xs">Add Product</Label>
                <Select value={addProductId} onValueChange={setAddProductId}>
                  <SelectTrigger data-testid="create-add-product-select">
                    <SelectValue placeholder="Select product..." />
                  </SelectTrigger>
                  <SelectContent>
                    {availableProducts.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name} (${p.retail_price?.toFixed(2)}) — Stock: {p.quantity_in_stock}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                size="sm"
                onClick={addProduct}
                disabled={!addProductId}
                className="bg-teal hover:bg-teal/90"
                data-testid="create-add-product-btn"
              >
                <Plus className="w-4 h-4 mr-1" /> Add
              </Button>
            </div>
          </div>

          <div>
            <Label>Notes</Label>
            <Input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Order notes (optional)"
              data-testid="create-order-notes"
            />
          </div>

          {/* Total */}
          {items.length > 0 && (
            <div className="flex justify-end pt-2 border-t">
              <p className="text-lg font-bold text-navy">Total: ${total.toFixed(2)}</p>
            </div>
          )}

          <Button
            onClick={handleCreate}
            disabled={submitting || !dealerId || items.length === 0}
            className="w-full bg-teal hover:bg-teal/90"
            data-testid="create-order-submit-btn"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create Order"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ===== Main Component =====
export default function AdminMarketplaceOrders({ embedded = false }) {
  const { user } = useAuth();
  const canDelete = user?.role === "admin" || (`${user?.first_name || ""} ${user?.last_name || ""}`.trim().toLowerCase() === "ray kalasho");
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [editOrder, setEditOrder] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [dealers, setDealers] = useState([]);
  const [products, setProducts] = useState([]);
  const [deletingId, setDeletingId] = useState(null);

  const fetchOrders = useCallback(async () => {
    try {
      const data = await getAllMarketplaceOrders(statusFilter === "all" ? null : statusFilter);
      setOrders(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [statusFilter]);

  const fetchMeta = useCallback(async () => {
    try {
      const [d, p] = await Promise.all([getDealersForOrder(), getInventoryProducts()]);
      setDealers(d);
      setProducts(p);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);
  useEffect(() => { fetchMeta(); }, [fetchMeta]);

  const openEdit = (order) => {
    setEditOrder(order);
    setEditOpen(true);
  };

  const handleDeleteOrder = async (e, order) => {
    e.stopPropagation();
    if (!window.confirm(`Hard-delete order ${order.order_number}?\n\nThis is a record-cleanup action and does NOT restock inventory. Use "Cancel" instead if you want inventory handling.`)) return;
    setDeletingId(order.id);
    try {
      await deleteMarketplaceOrder(order.id);
      toast.success("Order deleted");
      fetchOrders();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete order");
    } finally { setDeletingId(null); }
  };

  if (loading) {
    return (
      <OrdersLayout embedded={embedded}>
        <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-teal" /></div>
      </OrdersLayout>
    );
  }

  const counts = {
    all: orders.length,
    pending: orders.filter((o) => o.status === "pending").length,
    processing: orders.filter((o) => o.status === "processing").length,
    fulfilled: orders.filter((o) => o.status === "fulfilled").length,
  };

  return (
    <OrdersLayout embedded={embedded}>
      <div className="space-y-6" data-testid="admin-marketplace-orders">
        {/* Summary cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card className="border-slate-200"><CardContent className="py-3 px-4"><p className="text-xs text-slate-500 uppercase tracking-wide">Total Orders</p><p className="text-2xl font-bold text-navy">{counts.all}</p></CardContent></Card>
          <Card className="border-amber-200 bg-amber-50"><CardContent className="py-3 px-4"><p className="text-xs text-amber-600 uppercase tracking-wide">Pending</p><p className="text-2xl font-bold text-amber-700">{counts.pending}</p></CardContent></Card>
          <Card className="border-blue-200 bg-blue-50"><CardContent className="py-3 px-4"><p className="text-xs text-blue-600 uppercase tracking-wide">Processing</p><p className="text-2xl font-bold text-blue-700">{counts.processing}</p></CardContent></Card>
          <Card className="border-emerald-200 bg-emerald-50"><CardContent className="py-3 px-4"><p className="text-xs text-emerald-600 uppercase tracking-wide">Fulfilled</p><p className="text-2xl font-bold text-emerald-700">{counts.fulfilled}</p></CardContent></Card>
        </div>

        {/* Filter Tabs + Create */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex flex-wrap gap-2" data-testid="status-filter-tabs">
            {[
              { value: "all", label: "All Orders", count: counts.all },
              { value: "pending", label: "Pending", count: counts.pending },
              { value: "processing", label: "Processing", count: counts.processing },
              { value: "fulfilled", label: "Fulfilled", count: counts.fulfilled },
              { value: "cancelled", label: "Cancelled", count: orders.filter((o) => o.status === "cancelled").length },
            ].map((t) => (
              <button
                key={t.value}
                onClick={() => setStatusFilter(t.value)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${statusFilter === t.value ? "bg-navy text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"}`}
                data-testid={`status-tab-${t.value}`}
              >
                {t.label} {t.count > 0 && <span className="ml-1 text-xs opacity-75">({t.count})</span>}
              </button>
            ))}
          </div>
          <Button
            onClick={() => setCreateOpen(true)}
            className="bg-teal hover:bg-teal/90"
            data-testid="create-order-btn"
          >
            <PlusCircle className="w-4 h-4 mr-2" /> Create Order
          </Button>
        </div>

        {/* Orders list */}
        <div className="space-y-3">
          {orders.map((order) => {
            const StatusIcon = statusIcons[order.status] || Clock;
            return (
              <Card key={order.id} className="border-slate-200 hover:border-slate-300 transition-colors">
                <CardContent className="py-4 px-4">
                  <div className="flex flex-col sm:flex-row justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <p className="font-medium text-navy" data-testid={`order-num-${order.id}`}>{order.order_number}</p>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${statusColors[order.status]}`}>
                          <StatusIcon className="w-3 h-3" />
                          {order.status}
                        </span>
                        {order.created_by_admin && (
                          <span className="text-xs bg-violet-100 text-violet-600 px-2 py-0.5 rounded">Admin Created</span>
                        )}
                      </div>
                      <p className="text-sm text-slate-600">{order.dealer_name} ({order.dealer_email})</p>
                      <p className="text-xs text-slate-400 mt-1">{new Date(order.created_at).toLocaleString()}</p>
                      <div className="mt-2">
                        {order.items?.map((item, i) => (
                          <div key={i} className="flex items-center gap-2 text-sm text-slate-500">
                            <span>{item.product_name} x{item.quantity} @ ${item.unit_price?.toFixed(2)}</span>
                            {item.assigned_serials?.length > 0 && (
                              <span className="text-xs bg-teal/10 text-teal px-1.5 py-0.5 rounded">
                                {item.assigned_serials.length} serials
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                      {order.tracking_number && <p className="text-xs text-blue-600 mt-1">Tracking: {order.tracking_number}</p>}
                      {order.fulfilled_by && <p className="text-xs text-emerald-600 mt-1">Fulfilled by: {order.fulfilled_by}</p>}
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <p className="font-bold text-navy text-lg">${order.total?.toFixed(2)}</p>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openEdit(order)}
                          data-testid={`edit-order-${order.id}`}
                          className="gap-1"
                        >
                          <Edit className="w-3 h-3" /> Edit
                        </Button>
                        {canDelete && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-500 hover:bg-red-50 hover:text-red-600"
                            disabled={deletingId === order.id}
                            onClick={(e) => handleDeleteOrder(e, order)}
                            data-testid={`delete-order-${order.id}`}
                          >
                            {deletingId === order.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
          {orders.length === 0 && <p className="text-center text-slate-400 py-12">No marketplace orders yet</p>}
        </div>

        {/* Edit Order Dialog */}
        <EditOrderDialog
          order={editOrder}
          open={editOpen}
          onClose={() => setEditOpen(false)}
          onSaved={fetchOrders}
          products={products}
        />

        {/* Create Order Dialog */}
        <CreateOrderDialog
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          onCreated={fetchOrders}
          dealers={dealers}
          products={products}
        />
      </div>
    </OrdersLayout>
  );
}

function OrdersLayout({ embedded, children }) {
  if (embedded) return <>{children}</>;
  return (
    <AdminLayout title="Marketplace Orders" breadcrumb="Marketplace Orders">
      {children}
    </AdminLayout>
  );
}
